import { useLocation, useNavigate } from 'react-router-dom';
import { useRef, useState } from 'react';
import {
    Box,
    Button,
    Paper,
    Typography,
    Stack,
    TextField,
    FormControlLabel,
    Checkbox,
    Tooltip,
    IconButton,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import LectureSelectionForm from '../../components/LectureSelectionForm';
import PaymentOption from '../../components/PaymentOption';
import axios from 'axios';

const SERVER = process.env.REACT_APP_SERVER;

function Application() {
    const navigate = useNavigate();
    const location = useLocation();
    const { lectureId, lectureTitle } = location.state || {};
    const [selectedFees, setSelectedFees] = useState([]);
    const [selectedFeeNames, setSelectedFeeNames] = useState([]);
    const [total, setTotal] = useState(0);
    const [feeError, setFeeError] = useState(false);
    const feeErrorRef = useRef(null);
    const [payMethod, setPayMethod] = useState('카드');
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        contact: '',
        isConsentAgreed: false,
    });

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleContactChange = (e) => {
        const digits = e.target.value.replace(/\D/g, '').slice(0, 11);
        let formatted = digits;
        if (digits.length > 7) {
            formatted = `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
        } else if (digits.length > 3) {
            formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
        }
        setFormData((prev) => ({ ...prev, contact: formatted }));
    };

    const handleLectureChange = (fees, total, names) => {
        setSelectedFees(fees);
        setSelectedFeeNames(names);
        setTotal(total);
    };

    const handleMethodSelect = (pay) => {
        setPayMethod(pay);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (selectedFees.length === 0) {
            setFeeError(true);
            setTimeout(() => {
                if (feeErrorRef.current) {
                    feeErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 10);
            return;
        } else {
            setFeeError(false);
        }

        const orderName =
            selectedFeeNames.length === 1
                ? selectedFeeNames[0]
                : `${selectedFeeNames[0]} 외 ${selectedFeeNames.length - 1}건`;

        const data = {
            applicant: {
                name: formData.name,
                email: formData.email,
                birth: null,
                contact: formData.contact,
                agree: formData.isConsentAgreed,
            },
            lectureId,
            lectureTitle,
            selectedFees,
            totalAmount: total,
        };

        axios
            .post(`${SERVER}/api/lecture/application/data`, data)
            .then((res) => {
                const isValid = res.data.state;
                if (isValid == 0) {
                    navigate('/payments', {
                        state: {
                            method: payMethod,
                            amount: total,
                            orderName,
                            customerName: formData.name,
                            customerEmail: formData.email,
                            applicationId: res.data.applicationId,
                        },
                    });
                } else if (isValid == -1) {
                    alert('해당 강의를 신청한 이메일이 있습니다.');
                }
            })
            .catch((err) => {
                console.error('에러 발생: ', err);
            });
    };

    return (
        <Box
            className="application"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            <Box sx={{ maxWidth: '60vh' }}>
                <div className="app-title">
                    <Typography fontSize="2rem" fontWeight="bold" sx={{ mt: 5, mb: 4 }}>
                        {' '}
                        {lectureTitle}{' '}
                    </Typography>
                </div>
                <form onSubmit={handleSubmit}>
                    <LectureSelectionForm
                        lectureId={lectureId}
                        selectedFees={selectedFees}
                        total={total}
                        onChange={handleLectureChange}
                        feeError={feeError}
                        errorRef={feeErrorRef}
                    />
                    <Paper
                        variant="outlined"
                        sx={{
                            backgroundColor: '#dddddd',
                            border: '2px solid #2196f3',
                            p: 2,
                            mt: 2,
                            mb: 2,
                            maxHeight: 180,
                            overflowY: 'auto',
                        }}
                    >
                        <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                            환불 규정
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                            1. 수강생이 강의 개시일 5일 전(공휴일 및 토요일 포함, 이하 동일)까지
                            환불을 요청하는 경우, 이미 납부한 수강료 전액을 환불합니다. {'\n'}
                            2. 수강생이 강의 개시일 4일 전부터 강의 개시일 전까지 환불을
                            요청하는 경우, 이미 납부한 수강료의 50%를 환불합니다.{'\n'}
                            3. 강의 개시일 당일과 이후에는 환불이 불가합니다.{'\n'}※ 강의
                            개시일이란 실제 강의가 최초로 시작되는 날을 의미합니다.{'\n'}※ 환불
                            요청은 서면(전자우편 등), 전화, 문자메시지 등 본인의 의사가 명확히
                            확인 가능한 방법으로 접수된 시점을 기준으로 처리됩니다.
                        </Typography>
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 3, mt: 2 }}>
                        <Typography mb={2} fontWeight="bold">
                            신청자 정보 입력
                        </Typography>
                        <Stack spacing={2}>
                            <TextField
                                label="이름"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                fullWidth
                            />
                            <TextField
                                label="연락처"
                                name="contact"
                                value={formData.contact}
                                onChange={handleContactChange}
                                placeholder="010-0000-0000"
                                required
                                fullWidth
                            />
                            <TextField
                                label="이메일"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                fullWidth
                            />
                        </Stack>
                        <FormControlLabel
                            control={
                                <Checkbox name="isConsentAgreed" checked={formData.isConsentAgreed} onChange={handleChange} />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    결제 동의
                                    <Tooltip title="원활한 강의 신청 및 결제 처리를 위해 이름과 이메일 주소를 수집합니다. 해당 데이터는 강의 기간동안 유지 됩니다. 강의 종료 후 파기됩니다." arrow>
                                        <IconButton size="small" sx={{ p: 0 }}>
                                            <HelpOutlineIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            }
                            sx={{ mt: 1 }}
                        />
                    </Paper>

                    <PaymentOption onSelect={handleMethodSelect} />

                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 10 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={
                                !formData.name ||
                                !formData.contact ||
                                !formData.email ||
                                !formData.isConsentAgreed ||
                                selectedFees.length === 0
                            }
                            sx={{ width: 120, fontWeight: 'bold', fontSize: '1rem' }}
                        >
                            신청
                        </Button>
                    </Box>
                </form>
            </Box>
        </Box>
    );
}

export default Application;
